import { Link } from 'react-router-dom';
import { MarketplaceArche } from '../../lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArcheIconImg, DefaultArcheIcon } from '@/lib/arche-icon-url';

interface ArcheCardProps {
  arche: MarketplaceArche;
}

export function ArcheCard({ arche }: ArcheCardProps) {
  const pricingLabel =
    arche.pricing.model === 'included'
      ? 'Included'
      : arche.pricing.model === 'no_base_cost'
        ? 'No Base Cost'
        : arche.pricing.model === 'free'
          ? 'Included' // Deprecated 'free' maps to 'Included'
          : arche.pricing.model === 'fixed' && arche.pricing.priceCents
            ? `$${(arche.pricing.priceCents / 100).toFixed(2)}`
            : arche.pricing.model === 'per_use'
              ? 'Pay per use'
              : 'Variable';

  return (
    <Link to={`/marketplace/arches/${arche.slug || arche.archId}`} className="block h-full">
      <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
        <CardContent className="p-4 flex flex-col flex-1">
          {/* Square icon */}
          <div className="flex justify-center mb-3">
            {arche.imageRef ? (
              <ArcheIconImg
                imageRef={arche.imageRef}
                size={128}
                alt={arche.name}
                className="w-16 h-16 rounded-2xl object-cover shadow-sm"
              />
            ) : (
              <DefaultArcheIcon className="w-16 h-16 rounded-2xl shadow-sm" />
            )}
          </div>

          {/* Name */}
          <h3 className="text-sm font-semibold text-foreground text-center line-clamp-2 mb-1">
            {arche.name}
          </h3>

          <p className="text-sm text-muted-foreground line-clamp-3 mb-3 min-h-[3.75rem] text-center">
            {arche.simpleDescription}
          </p>

          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2 mt-auto">
            {arche.categoryName && <Badge variant="outline">{arche.categoryName}</Badge>}
            <div className="flex items-center gap-3">
              <span title="Fias Fitness Score">FFS: {(arche.fitnessScore * 100).toFixed(0)}%</span>
              <span title="Times Used">{arche.invocationCount.toLocaleString()} runs</span>
            </div>
          </div>

          <div className="flex justify-end gap-1.5">
            {arche.hasPerUseCredits && (
              <Badge variant="outline" className="text-xs">
                Credits per use
              </Badge>
            )}
            <Badge
              variant={
                arche.pricing.model === 'free' || arche.pricing.model === 'included'
                  ? 'secondary'
                  : 'default'
              }
            >
              {pricingLabel}
            </Badge>
            {arche.pricing.model === 'no_base_cost' && (
              <span className="text-[10px] text-muted-foreground">AI usage fees apply</span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
